import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    FiAlertCircle,
    FiAlertTriangle,
    FiCheckCircle,
    FiEdit3,
    FiInfo,
    FiSettings,
    FiX,
} from "react-icons/fi";
import MessageContent from "../MessageContent";
import MessageInput from "../MessageInput";
import FollowupSuggestions from "../FollowupSuggestions";
import ActionControllerComponent from "../ActionControllerComponent";
import CitationsComponent from "../CitationsComponent";
import Tooltip from "../Tooltip";
import AssistantSettingsPanel from "../AssistantSettingsPanel";
import { AIAssistantAblyProvider } from "../../contexts/AIAssistantAblyContext";
import AssistantSocketListener from "../AssistantSocketListener";
import {
    selectIsChatOpen,
    selectIsChatExpanded,
} from "../../Selectors/AssistantSelector";
import {
    toggleChatVisibility,
    setChatExpanded,
    assistantResponding,
    deleteMessagesKeepingInitial,
    hideCitations,
    clearCitations,
    clearHistoryMetadata,
    clearFollowupSuggestions,
    setTaskStatusMessage,
    setWelcomeSuggestions,
    clearWelcomeSuggestions,
} from "../../state/actions";
import { initiateSession } from "../../utils/SessionManager";
import { useScreenContext } from "../../hooks/useScreenContext";
import { useAssistantSettingsState } from "../../hooks/useAssistantSettingsState";
import { AI_ASSISTANT_CONSTANTS } from "../../constants/aiAssistantConstants";
import { AI_ASSISTANT_PENDO_EVENTS, trackAssistantEvent } from "../../utils/assistantAnalytics";
import { isAssistantMultilingualEnabled } from "../../utils/assistantEnvironment";
import "./ChatWindow.scss";

const ToastStack = ({ toasts, onClose }) => {
    const typeStyles = {
        success: { icon: <FiCheckCircle />, className: "success", defaultDescription: "The action completed successfully." },
        error: { icon: <FiAlertCircle />, className: "error", defaultDescription: "Something went wrong." },
        warning: { icon: <FiAlertTriangle />, className: "warning", defaultDescription: "Please review this warning." },
        info: { icon: <FiInfo />, className: "info", defaultDescription: "FYI." },
    };

    return (
        <div className="chat-toast-stack">
            {toasts.map((toast) => {
                const style = typeStyles[toast.type] || typeStyles.info;
                return (
                    <div
                        key={toast.id}
                        className={`chat-toast-card ${style.className}`}
                    >
                        <div className="chat-toast-icon">{style.icon}</div>
                        <div className="chat-toast-content">
                            <div className="chat-toast-title">{toast.title}</div>
                            <div className="chat-toast-description">
                                {toast.description || style.defaultDescription}
                            </div>
                        </div>
                        <button
                            className="chat-toast-close"
                            type="button"
                            aria-label="Close toast"
                            onClick={() => onClose(toast.id)}
                        >
                            <FiX />
                        </button>
                    </div>
                );
            })}
        </div>
    );
};

const ChatWindowContent = () => {
    const dispatch = useDispatch();
    const isChatOpen = useSelector(selectIsChatOpen);
    const isExpanded = useSelector(selectIsChatExpanded);
    const messages = useSelector((state) => state.chat.messages);
    const userDetail = useSelector((state) => state.userDetail);
    const {
        citations,
        isActionControllerVisible,
        isCitationsExpanded,
        actionControllerContent,
        followupSuggestions = [],
    } = useSelector((state) => state.chat);
    const hasMessages = messages.length > 0;
    const [clearState, setClearState] = useState("idle");
    const [toasts, setToasts] = useState([]);
    const [isScrolledState, setIsScrolledState] = useState(false);
    const { buildCurrentScreenContext } = useScreenContext();
    const assistantSettings = useAssistantSettingsState({
        userId: userDetail?.id,
    });
    const isMultilingualAssistantEnabled = isAssistantMultilingualEnabled();

    React.useEffect(() => {
        const handleScrollState = (e) => {
            setIsScrolledState(e.detail.isScrolled);
        };
        window.addEventListener("chat-scroll-state", handleScrollState);
        return () => window.removeEventListener("chat-scroll-state", handleScrollState);
    }, []);

    const addToast = ({ type = "info", title, description, duration = 2500 }) => {
        const id = Math.random().toString(36).slice(2);
        const toast = { id, type, title, description };
        setToasts((prev) => [...prev, toast]);
        if (duration !== null) {
            setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== id));
            }, duration);
        }
        return id;
    };

    const updateToast = (id, updates) => {
        setToasts((prev) =>
            prev.map((t) => (t.id === id ? { ...t, ...updates } : t))
        );
        if (updates.duration !== undefined && updates.duration !== null) {
            setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== id));
            }, updates.duration);
        }
    };
    const handleClearChat = async () => {
        trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.NEW_CHAT_CLICKED);
        setClearState("clearing");
        try {
            const currentScreenContext = buildCurrentScreenContext();
            const preferences = isMultilingualAssistantEnabled
                ? assistantSettings.savedPreferences
                : undefined;
            const res = await initiateSession({
                userId: userDetail?.id,
                currentScreenContext,
                ...(preferences && { preferences }),
            });
            if (res?.success) {
                dispatch(deleteMessagesKeepingInitial(0));
                dispatch(clearCitations());
                dispatch(clearHistoryMetadata());
                dispatch(clearFollowupSuggestions());
                if (res.suggestions) {
                    dispatch(setWelcomeSuggestions(res.suggestions));
                } else {
                    dispatch(clearWelcomeSuggestions());
                }
                dispatch(assistantResponding(false));
                dispatch(setTaskStatusMessage({ type: "task_status_end" }));
                setClearState("idle");
                addToast({
                    type: "success",
                    title: "New chat ready.",
                    description: "You can start typing your next question.",
                    duration: 2000,
                });
                trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.NEW_CHAT_STARTED);
            } else {
                throw new Error("Clear chat failed");
            }
        } catch (error) {
            setClearState("failed");
            trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.NEW_CHAT_FAILED);
            addToast({
                type: "error",
                title: "Failed to start a new chat.",
                description: "Please try again.",
                duration: 2500,
            });
            setTimeout(() => {
                setClearState("idle");
            }, 2000);
        }
    };

    const toggleCitations = () => {
        if (isCitationsExpanded) {
            dispatch(hideCitations());
        }
    }

    const handleSuggestionClick = (text) => {
        if (!text) return;
        trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.FOLLOWUP_CLICKED);
        window.dispatchEvent(
            new CustomEvent("ai-assistant-send-suggestion", { detail: { text } })
        );
    };

    const handleExpand = () => {
        dispatch(setChatExpanded(!isExpanded));
    };

    const handleClose = () => {
        trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.CLOSED);
        dispatch(toggleChatVisibility(false));
        dispatch(setChatExpanded(false));
        assistantSettings.closeSettings();
    };

    return (
        <div
            className={` card-container chat-container
    ${isExpanded ? "expanded" : ""}
    ${!isChatOpen ? "collapsed" : ""}
    ${isActionControllerVisible ? "action-controller-visible" : ""}
    ${assistantSettings.theme === "dark" ? "ai-dark" : ""}`}
        >


            <div className="card-header">
                <div className="card-title">
                    <span>{AI_ASSISTANT_CONSTANTS.CHAT.TITLE}</span>
                    <span className="beta-tag" aria-label="Assistant version">
                        {AI_ASSISTANT_CONSTANTS.CHAT.VERSION}
                    </span>
                </div>

                <div className="card-header-actions">
                    {isMultilingualAssistantEnabled && (
                        <button
                            type="button"
                            className={`icon-button ${
                                assistantSettings.isSettingsOpen ? "is-active" : ""
                            }`}
                            onClick={() =>
                                assistantSettings.isSettingsOpen
                                    ? assistantSettings.closeSettings()
                                    : assistantSettings.openSettings()
                            }
                            aria-label="Open settings"
                            aria-pressed={assistantSettings.isSettingsOpen}
                        >
                            <Tooltip content="Settings" placement="bottom">
                                <span>
                                    <FiSettings size={16} />
                                </span>
                            </Tooltip>
                        </button>
                    )}

                    <button
                        type="button"
                        className={`icon-button ${clearState !== "idle" ? "disabled" : ""}`}
                        onClick={clearState === "idle" ? handleClearChat : undefined}
                        disabled={clearState !== "idle"}
                        aria-label="Start new chat"
                    >
                        <Tooltip content="New chat" placement="bottom">
                            <span>
                                <FiEdit3 size={16} />
                            </span>
                        </Tooltip>
                    </button>

                    <button type="button" className="icon-button" onClick={handleClose} aria-label="Close chat">
                        <Tooltip content="Close" placement="bottom">
                            <span>
                                <FiX size={16} />
                            </span>
                        </Tooltip>
                    </button>
                </div>
            </div>

            <div className={`chat-body ${hasMessages ? "has-messages" : "no-messages"}`}>
                <MessageContent />
                <div className={`chat-footer-overlay ${isScrolledState ? "is-scrolled" : ""}`}>
                    {followupSuggestions.length > 0 && (
                        <div className="followup-suggestions-footer">
                            <FollowupSuggestions
                                suggestions={followupSuggestions}
                                onSuggestionClick={handleSuggestionClick}
                            />
                        </div>
                    )}
                    <MessageInput />
                </div>
            </div>
            {isMultilingualAssistantEnabled && assistantSettings.isSettingsOpen && (
                <div
                    className="assistant-settings-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Assistant settings"
                >
                    <AssistantSettingsPanel
                        preferencesDraft={assistantSettings.preferencesDraft}
                        theme={assistantSettings.theme}
                        onPreferencesChange={assistantSettings.setPreferencesDraft}
                        onClose={assistantSettings.closeSettings}
                    />
                </div>
            )}
            {isActionControllerVisible && (
                <ActionControllerComponent
                    title="Information Sources"
                    isVisible={isCitationsExpanded}
                    isExpanded={isCitationsExpanded}
                    toggleExpand={toggleCitations}
                >
                    {actionControllerContent === 'Citations' && <CitationsComponent citations={citations} />}
                </ActionControllerComponent>
            )}
            <ToastStack
                toasts={toasts}
                onClose={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))}
            />
        </div>
    );
};

const ChatWindow = () => (
    <AIAssistantAblyProvider>
        <AssistantSocketListener />
        <ChatWindowContent />
    </AIAssistantAblyProvider>
);

export default ChatWindow;
